/**
 * Bounded calculator-engagement vocabulary for the four measured calculator
 * paths (w1/m174/t001, extended with break-even in w1/m181/t005).
 *
 * Pure and client-safe: no network, no filesystem, no `process`, no
 * script/server imports. The two founder pages encode through
 * `src/lib/founder-calculator-analytics.ts`; the conversion report decodes
 * through the parsers below. GA emission stays behind `emitGaEvent`, and the
 * report joins on the already registered `customEvent:event_label` dimension —
 * no new custom dimension is required.
 *
 * Historical contracts are preserved verbatim, not redefined here:
 * `debt_coverage` labels stay `${action}_fccr_dscr` (owned by
 * `src/lib/debt-coverage-analytics.ts`) and converter `tool_use` keeps its
 * twelve frozen labels (owned by `src/lib/tools-analytics.ts`). The literals
 * below are the report's parse table — the same arrangement
 * `src/lib/conversion-report.ts` uses — and the compatibility fixtures pin
 * them byte-equal to their owners, so a rename there fails loudly here.
 *
 * Reporting interpretation: every decoded event is an explicit-action count,
 * never a unique user, a signup, a saved file, or a causal funnel step.
 * Unknown event names, labels and paths decode to `null` so the report can
 * bucket them visibly instead of inflating a known column.
 */

/** Event name for debt-coverage engagement (owner: debt-coverage-analytics). */
export const DEBT_COVERAGE_EVENT_NAME = "debt_coverage" as const;

/** Event name for founder-calculator engagement. */
export const FOUNDER_CALCULATOR_EVENT_NAME = "founder_calculator" as const;

/** Event name for break-even engagement (owner: break-even-analytics). */
export const BREAK_EVEN_EVENT_NAME = "break_even" as const;

/** Actions the debt calculator emits, in label order. */
const DEBT_COVERAGE_ACTIONS = [
  "calculate",
  "example",
  "download",
  "cta",
] as const;

type DebtCoverageEngagementAction =
  (typeof DEBT_COVERAGE_ACTIONS)[number];

/** Formula family the debt labels carry — small enum only, never inputs. */
const DEBT_COVERAGE_FORMULA = "fccr_dscr" as const;

/**
 * Rebuild a historical debt label verbatim (`${action}_fccr_dscr`).
 * Byte-equal to `debtCoverageEventParams(action).event_label` by fixture.
 */
export function debtCoverageEngagementLabel(
  action: DebtCoverageEngagementAction,
): string {
  return `${action}_${DEBT_COVERAGE_FORMULA}`;
}

/** Bounded founder tool identifiers. */
export const FOUNDER_CALCULATOR_TOOLS = ["equity", "hiring"] as const;

export type FounderCalculatorTool = (typeof FOUNDER_CALCULATOR_TOOLS)[number];

/**
 * Bounded founder actions — explicit intents only:
 * `scenario_export` fires after a valid scenario export is prepared,
 * `scenario_apply` after the reader explicitly applies a validated staged
 * scenario, `summary_download` after a valid summary download is initiated.
 * Render/effect/keystroke activity, automatic recomputation, invalid or
 * cross-tool files, canceled staging and failed validation are not events.
 */
export const FOUNDER_CALCULATOR_ACTIONS = [
  "scenario_export",
  "scenario_apply",
  "summary_download",
] as const;

export type FounderCalculatorAction =
  (typeof FOUNDER_CALCULATOR_ACTIONS)[number];

/**
 * Deterministic founder label (`${tool}_${action}`, six values).
 * Carries no amounts, currencies, share values, filenames, account names or
 * arbitrary input — only the two bounded enums above.
 */
export function founderCalculatorEventLabel(
  tool: FounderCalculatorTool,
  action: FounderCalculatorAction,
): string {
  return `${tool}_${action}`;
}

/** Actions the break-even calculator emits, in label order. */
const BREAK_EVEN_ACTIONS = [
  "calculate",
  "example",
  "download",
  "cta",
] as const;

type BreakEvenEngagementAction = (typeof BREAK_EVEN_ACTIONS)[number];

/** Formula family the break-even labels carry — small enum only, never inputs. */
const BREAK_EVEN_FORMULA = "contribution_margin" as const;

/**
 * Rebuild a break-even label verbatim (`${action}_contribution_margin`).
 * Byte-equal to `breakEvenEventParams(action).event_label` by fixture.
 */
export function breakEvenEngagementLabel(
  action: BreakEvenEngagementAction,
): string {
  return `${action}_${BREAK_EVEN_FORMULA}`;
}

/** The four measured logical paths (locale-stripped, query excluded). */
const DEBT_COVERAGE_LOGICAL_PATH =
  "/tools/debt-coverage-calculator" as const;
const FOUNDER_EQUITY_LOGICAL_PATH =
  "/founder-resources/founder-equity-calculator" as const;
const HIRING_LOGICAL_PATH =
  "/founder-resources/can-i-afford-to-hire-an-employee-calculator" as const;
const BREAK_EVEN_LOGICAL_PATH = "/tools/break-even-calculator" as const;

export const CALCULATOR_LOGICAL_PATHS = [
  DEBT_COVERAGE_LOGICAL_PATH,
  FOUNDER_EQUITY_LOGICAL_PATH,
  HIRING_LOGICAL_PATH,
  BREAK_EVEN_LOGICAL_PATH,
] as const;

/** Which calculator a logical path belongs to — the report's numerator scope. */
export type CalculatorTool =
  | "debt_coverage"
  | "break_even"
  | FounderCalculatorTool;

const TOOL_BY_LOGICAL_PATH: Readonly<Record<string, CalculatorTool>> = {
  [DEBT_COVERAGE_LOGICAL_PATH]: "debt_coverage",
  [FOUNDER_EQUITY_LOGICAL_PATH]: "equity",
  [HIRING_LOGICAL_PATH]: "hiring",
  [BREAK_EVEN_LOGICAL_PATH]: "break_even",
};

/**
 * Map a locale-stripped logical page to its calculator, or `null` when it is
 * not one of the four measured paths. A trailing meaningful query rides
 * along on report pages, so it is stripped before the exact match; external
 * mounts, locale prefixes and same-suffix lookalikes never match because only
 * these four exact pathnames qualify.
 */
export function calculatorToolForLogicalPage(
  logicalPage: string,
): CalculatorTool | null {
  const pathname = logicalPage.split("?", 1)[0]?.trim() ?? "";
  return TOOL_BY_LOGICAL_PATH[pathname] ?? null;
}

/** A successfully decoded calculator event — known tool/action only. */
type KnownCalculatorEvent =
  | { family: "debt_coverage"; action: DebtCoverageEngagementAction }
  | {
      family: "founder_calculator";
      tool: FounderCalculatorTool;
      action: FounderCalculatorAction;
    }
  | { family: "break_even"; action: BreakEvenEngagementAction };

/**
 * Decode a debt label to its action, or `null` for anything else — including
 * founder and converter labels, so families can never be mistaken for each
 * other.
 */
export function parseDebtCoverageEngagementLabel(
  label: string,
): DebtCoverageEngagementAction | null {
  const trimmed = label.trim();
  for (const action of DEBT_COVERAGE_ACTIONS) {
    if (trimmed === debtCoverageEngagementLabel(action)) return action;
  }
  return null;
}

/**
 * Decode a founder label to its tool/action pair, or `null` for anything
 * else. Only the six `${tool}_${action}` combinations qualify: bare tool
 * names, bare actions, overlong suffixes and foreign labels fail closed.
 */
export function parseFounderCalculatorEventLabel(label: string): {
  tool: FounderCalculatorTool;
  action: FounderCalculatorAction;
} | null {
  const trimmed = label.trim();
  for (const tool of FOUNDER_CALCULATOR_TOOLS) {
    if (!trimmed.startsWith(`${tool}_`)) continue;
    const action = trimmed.slice(tool.length + 1);
    if ((FOUNDER_CALCULATOR_ACTIONS as readonly string[]).includes(action)) {
      return { tool, action: action as FounderCalculatorAction };
    }
    return null;
  }
  return null;
}

/**
 * Decode a break-even label to its action, or `null` for anything else —
 * including debt, founder and converter labels, so families can never be
 * mistaken for each other.
 */
export function parseBreakEvenEngagementLabel(
  label: string,
): BreakEvenEngagementAction | null {
  const trimmed = label.trim();
  for (const action of BREAK_EVEN_ACTIONS) {
    if (trimmed === breakEvenEngagementLabel(action)) return action;
  }
  return null;
}

/**
 * Decode one GA event to a known calculator event, or `null` when it names
 * no known tool/action. The event name gates first: a `tool_use` row carrying
 * a debt-shaped label, or a `debt_coverage` row carrying a founder-shaped
 * label, is unknown — labels are only meaningful under their own event.
 */
export function classifyCalculatorEvent(
  eventName: string,
  eventLabel: string,
): KnownCalculatorEvent | null {
  if (eventName === DEBT_COVERAGE_EVENT_NAME) {
    const action = parseDebtCoverageEngagementLabel(eventLabel);
    return action == null ? null : { family: "debt_coverage", action };
  }
  if (eventName === FOUNDER_CALCULATOR_EVENT_NAME) {
    const decoded = parseFounderCalculatorEventLabel(eventLabel);
    return decoded == null
      ? null
      : { family: "founder_calculator", ...decoded };
  }
  if (eventName === BREAK_EVEN_EVENT_NAME) {
    const action = parseBreakEvenEngagementLabel(eventLabel);
    return action == null ? null : { family: "break_even", action };
  }
  return null;
}
